import { AsyncPipe } from "@angular/common";
import {
    Component,
    computed,
    effect,
    EventEmitter,
    inject,
    input,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewEncapsulation,
    ChangeDetectionStrategy
} from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { FormGroup, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { ActionButtonComponent } from "@components/action-button/action-button.component";
import { CheckboxComponent } from "@components/checkbox/checkbox.component";
import { IconButtonComponent } from "@components/icon-button/icon-button.component";
import { InputComponent } from "@components/input/input.component";
import { SelectorLabelBindingDirective } from "@components/selector/selector-label-binding.directive";
import { SelectorValueBindingDirective } from "@components/selector/selector-value-binding.directive";
import { SelectorComponent } from "@components/selector/selector.component";
import { NgbDropdown, NgbDropdownMenu, NgbDropdownToggle } from "@ng-bootstrap/ng-bootstrap";
import { isNil } from "lodash";
import moment from "moment";
import { combineLatest, firstValueFrom, Observable, Subject } from "rxjs";
import {
    defaultIfEmpty,
    map,
    shareReplay,
    skip,
    take,
    takeUntil,
    withLatestFrom
} from "rxjs/operators";
import { InputProcessorType } from "~app/common/components/input/input-processor";
import { MarknadsprislistaTyp, SenastAnvandaPrislistorFilterValues, VirkeskopskontraktPrisBerakningsTyper } from "~app/common/models/constants";
import { DateUtils } from "~app/common/utils/date-utils";
import { IMarknadsprislista } from "~app/features/virkeskop/models/marknadsprislista-model";
import { MarknadsprislistaFilter } from "~app/features/virkeskop/models/pristabell/marknadsprislistaFilter";
import { PristabellRad, RowHeaderAction } from "~app/features/virkeskop/models/pristabell/pristabell-models";
import { IHandelssortimentSenasteValdPrislista } from "~app/features/virkeskop/models/utskrift/utskrift-installningar";
import { SortimentSnittnettoRad } from "~app/features/virkeskop/models/vardeberakning/vardeberakning-models";
import { ForkalkylService } from "~app/features/virkeskop/services/forkalkyl.service";
import { KontraktMediatorService } from "~app/features/virkeskop/services/kontrakt-mediator.service";
import { UtskriftMediatorService } from "~app/features/virkeskop/services/utskrift-mediator.service";
import { UtskriftWebinstallningarService } from "~app/features/virkeskop/services/utskrift-webinstallningar.service";
import { canEdit } from "~app/role-permissions";
import { PrisradForm } from "../kontraktUtskriftForm";
interface PrislistaDetails {
    prislistaFilter: string;
    prislistaHistory: IHandelssortimentSenasteValdPrislista[];
}

@Component({
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: "[rask-kontrakt-prisrad]",
    templateUrl: "./kontrakt-prisrad.component.html",
    styleUrls: ["./kontrakt-prisrad.component.scss"],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        FormsModule,
        ReactiveFormsModule,
        CheckboxComponent,
        NgbDropdown,
        IconButtonComponent,
        NgbDropdownToggle,
        NgbDropdownMenu,
        ActionButtonComponent,
        SelectorComponent,
        SelectorValueBindingDirective,
        InputComponent,
        SelectorLabelBindingDirective,
        AsyncPipe
    ]
})
export class KontraktPrisradComponent implements OnInit, OnDestroy {
    allPrislistor: IMarknadsprislista[];
    @Input() formGroup: FormGroup<PrisradForm>;
    readonly prisRad = input<PristabellRad>();

    @Input() prislistaFilter$: Observable<string>;
    @Output() rowAction = new EventEmitter<RowHeaderAction>();

    protected prislistaOptions$: Observable<IMarknadsprislista[]>;
    private readonly destroy$ = new Subject<void>();
    private readonly forkalkylService = inject(ForkalkylService);
    private readonly kontraktMediatorService = inject(KontraktMediatorService);
    private readonly utskriftMediatorService = inject(UtskriftMediatorService);
    private readonly utskriftWebinstallningarService = inject(UtskriftWebinstallningarService);

    private readonly isReadonlyBase = toSignal(this.kontraktMediatorService.isReadonly$);
    private readonly isReadonly = computed(() => this.isReadonlyBase() || !this.canEdit());

    protected get isU6() {
        return this.kontraktMediatorService.kontrakt.isAffarsformU6;
    }

    protected get mattslagOptions$() {
        return this.utskriftMediatorService.mattslagOptions$;
    }

    protected get numberProcessor() {
        return InputProcessorType.numberIntProcessor;
    }

    protected get prisberakningOptions() {
        const options = [
            VirkeskopskontraktPrisBerakningsTyper.Prislista,
            VirkeskopskontraktPrisBerakningsTyper.Sortimentsnetto
        ];

        if (this.viol3 && !this.kontraktMediatorService.kontrakt.skordarmatning) {
            options.push(VirkeskopskontraktPrisBerakningsTyper.Prislistehanvisning);
        }

        return options;
    }

    protected get viol2() {
        return !this.kontraktMediatorService.kontrakt.viol3;
    }

    protected get viol3() {
        return this.kontraktMediatorService.kontrakt.viol3;
    }

    private get prislistaDetails$(): Observable<PrislistaDetails> {
        const allowedPrislistor$ = this.utskriftWebinstallningarService.senastValdPrislista$.pipe(
            withLatestFrom(this.prislistaOptions$),
            map(([prislistaHistory, prislistaOptions]) => prislistaHistory.filter(ph => prislistaOptions.some(po => po.id === ph.marknadsprislistaId)))
        );

        const handelssortimenPrislista$ = allowedPrislistor$.pipe(
            defaultIfEmpty([]),
            map(prislista => prislista
                .filter(hsp => (hsp.handelssortimentId === this.prisRad().handelssortiment_Id && hsp.handelssortimentId) ||
                    (hsp.handelssortimentsgruppId === this.prisRad().handelssortimentsgrupp_Id &&
                        !hsp.handelssortimentId && !this.prisRad().handelssortiment_Id && hsp.handelssortimentsgruppId))
                .sort((a, b) => a.datum && b.datum ? Date.parse(b.datum) - Date.parse(a.datum) : 0)
            ));

        return this.prislistaFilter$.pipe(withLatestFrom(handelssortimenPrislista$))
            .pipe(map(([
                prislistaFilter,
                prislistaHistory
            ]) => ({
                prislistaFilter,
                prislistaHistory
            })));
    }

    private readonly canEdit = canEdit();

    constructor() {
        effect(() => {
            const { extraMattslagsrad, autokompletterad } = this.prisRad();
            this.formGroup.enable({ emitEvent: false });
            if (extraMattslagsrad || this.isReadonly()) {
                this.formGroup.disable({ emitEvent: false });
            } else if (autokompletterad) {
                this.formGroup.controls.visasIUtskrift.disable({ emitEvent: false });
            }
        });
    }

    initHandelssortimentPrislista() {
        this.prislistaDetails$.pipe(take(1)).subscribe(x =>
            this.onHandelssortimenPrislistaChange(x));
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    ngOnInit(): void {
        this.prislistaOptions$ = combineLatest([
            this.utskriftMediatorService.prislistaOptions$,
            this.utskriftMediatorService.marknadsprislistaFilter$
        ]).pipe(
            map(([
                listor,
                prislistaFilter
            ]) => {
                this.allPrislistor = listor;
                return this.filterMarknadsprislisor(listor, prislistaFilter);
            }),
            shareReplay(1)
        );

        // skriv inte över filter på befintlig rad
        if (isNil(this.prisRad().id)) {
            this.initHandelssortimentPrislista();
        }

        this.prislistaDetails$.pipe(
            skip(1),
            takeUntil(this.destroy$)
        ).subscribe(x =>
            this.onHandelssortimenPrislistaChange(x));
    }

    async prisberakningChanged(prisberakning) {
        const {
            handelssortiment_Id,
            handelssortimentsgrupp_Id,
            tillaggAvdrag,
            sortimentsnetto,
            marknadsprislistaId,
            mattslag
        } = this.prisRad();

        const vardeberakning = await firstValueFrom(this.utskriftMediatorService.vardekalkylChanged$);
        const konverteringsfaktor = await firstValueFrom(this.forkalkylService.getAverageMattslag({
            produktionsomradeId: 26,
            mattslag: mattslag,
            handelssortimentId: handelssortiment_Id,
            handelssortimentsGruppId: handelssortimentsgrupp_Id
        }));

        const snittnettoPredicate: (rad: SortimentSnittnettoRad) => boolean = handelssortiment_Id
            ? x => handelssortiment_Id === x.handelssortimentId
            : x => handelssortimentsgrupp_Id === x.handelssortimentsgruppId && !handelssortiment_Id && !x.handelssortimentId;

        if (!vardeberakning) {
            return;
        }

        const snittnettoRad = vardeberakning.sortimentSnittnetto.rader.find(snittnettoPredicate);
        const useApris = this.kontraktMediatorService.kontrakt.isAffarsformU4 || this.kontraktMediatorService.kontrakt.isAffarsformU5;

        switch (prisberakning) {
            case VirkeskopskontraktPrisBerakningsTyper.Sortimentsnetto:
                this.prisRad().initSortimentsnetto(snittnettoRad, useApris);
                this.formGroup.patchValue({
                    tillaggAvdrag: tillaggAvdrag,
                    sortimentsnetto: Math.round(sortimentsnetto * konverteringsfaktor),
                    marknadsprislistaId: marknadsprislistaId
                });
                break;
            case VirkeskopskontraktPrisBerakningsTyper.Prislista:
                this.formGroup.patchValue({
                    tillaggAvdrag: snittnettoRad?.tillaggAvdrag > 0 ? Math.round(snittnettoRad?.tillaggAvdrag * konverteringsfaktor) : null,
                    sortimentsnetto: null
                });
                this.initHandelssortimentPrislista();
                break;
            case VirkeskopskontraktPrisBerakningsTyper.Prislistehanvisning:
                this.formGroup.patchValue({
                    tillaggAvdrag: snittnettoRad?.tillaggAvdrag > 0 ? Math.round(snittnettoRad?.tillaggAvdrag * konverteringsfaktor) : null,
                    sortimentsnetto: null,
                    marknadsprislistaId: null
                });
                this.initHandelssortimentPrislista();
                break;
        }
    }

    prislistaChanged(marknadsprislistaId) {
        const marknadsprislista = this.allPrislistor.find(x => x.id === marknadsprislistaId);
        this.prisRad().marknadsprislistaVidLeveransGallande = marknadsprislista?.vidLeveransGallande === true;
    }

    setMarknadsprislistaId(handelssortimenPrislista: IHandelssortimentSenasteValdPrislista) {
        let marknadsprislistaId: number = null;
        if (!isNil(handelssortimenPrislista)) {
            marknadsprislistaId = handelssortimenPrislista.marknadsprislistaId;
        }

        this.formGroup.patchValue({
            marknadsprislistaId: marknadsprislistaId
        });

        this.prisRad().marknadsprislistaVidLeveransGallande = handelssortimenPrislista?.vlg === true;
    }

    protected marknadsprislistaViewFormatter(item: IMarknadsprislista) {
        const vlg = item.vidLeveransGallande ? `${SenastAnvandaPrislistorFilterValues.VLG}, ` : "";
        const gallerfrom = moment(item.gallerFrom)
            .format(DateUtils.dateformat);

        return `${item.violId}, ${vlg}${item.benamning}, ${gallerfrom}`;
    }

    protected onRowAction(action: RowHeaderAction) {
        this.rowAction.emit(action);
    }

    protected priseberakningViewFormatter = (item) => {
        const useAPris = this.kontraktMediatorService.kontrakt.isAffarsformU4 || this.kontraktMediatorService.kontrakt.isAffarsformU5;
        if (item === VirkeskopskontraktPrisBerakningsTyper.Sortimentsnetto && useAPris) {
            return "á-pris";
        }

        if (item === VirkeskopskontraktPrisBerakningsTyper.Sortimentsnetto) {
            return "S.netto";
        }
        return item;
    };

    private filterMarknadsprislisor(marknadsprislistor: IMarknadsprislista[], marknadsprislistaFilter: MarknadsprislistaFilter) {
        const typer: string[] = [];
        const { handelssortimentsgrupp_Id, marknadsprislistaId } = this.prisRad();

        if (this.kontraktMediatorService.kontrakt.skordarmatning) {
            typer.push(MarknadsprislistaTyp.Skordarmatning);
        } else if (this.kontraktMediatorService.kontrakt.viol3) {
            typer.push(MarknadsprislistaTyp.Viol3);
        } else {
            typer.push(MarknadsprislistaTyp.Viol2);
        }

        return marknadsprislistaFilter.applyFilter(
            marknadsprislistor,
            handelssortimentsgrupp_Id,
            typer,
            marknadsprislistaId);
    }

    private onHandelssortimenPrislistaChange(details: PrislistaDetails) {
        const { prislistaFilter, prislistaHistory } = details;

        if (isNil(prislistaHistory) || prislistaHistory.length === 0) {
            this.setMarknadsprislistaId(null);
            return;
        }

        switch (prislistaFilter) {
            case SenastAnvandaPrislistorFilterValues.VLG: {
                const hsp = prislistaHistory.find(x => x.vlg);
                this.setMarknadsprislistaId(hsp);
                break;
            }
            case SenastAnvandaPrislistorFilterValues.IckeVLG: {
                const hsp = prislistaHistory.find(x => !x.vlg);
                this.setMarknadsprislistaId(hsp);
                break;
            }
            case SenastAnvandaPrislistorFilterValues.SenastAnvanda: {
                const hsp = prislistaHistory[0];
                this.setMarknadsprislistaId(hsp);
                break;
            }
        }
    }
}
